import { useRef, useState } from "react";
import { cn } from "@tellescope/ui";
import { useResizeObserver } from "@tellescope/react-hooks";
import { useAvailabilityRanges } from "../../hooks";
import { CommonCalendarOptions } from "../../types";
import { CalendarFilters } from "../../filters";
import { AvailabilityRangeButton } from "./AvailabilityRangeButton";

export interface AvailabilityHeaderRowProps extends CommonCalendarOptions {
  userId: string;
  filters?: CalendarFilters;
  /** Number of days beyond today to include. Defaults to 0 (current day only). */
  futureDayCount?: number;
  className?: string;
  getAvailabilityRangeClick: (startDate: Date) => (() => void) | { to: string; replace?: boolean };
}

export const AvailabilityHeaderRow = ({
  userId,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  filters,
  weekStart,
  futureDayCount,
  className,
  getAvailabilityRangeClick,
}: AvailabilityHeaderRowProps) => {
  const ranges = useAvailabilityRanges({ userId, timezone, filters, weekStart, futureDayCount, enabled: !!userId });

  const [isScrolling, setIsScrolling] = useState(false);
  const scrollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { ref: overflowRef, value: hasOverflow } = useResizeObserver<boolean, HTMLElement>(
    (element) => element.scrollWidth > element.clientWidth,
    false
  );

  const handleScroll = () => {
    setIsScrolling(true);
    if (scrollingTimeoutRef.current) clearTimeout(scrollingTimeoutRef.current);
    scrollingTimeoutRef.current = setTimeout(() => setIsScrolling(false), 1000);
  };

  if (ranges.length === 0) {
    return null;
  }

  return (
    <section
      ref={overflowRef}
      onScroll={handleScroll}
      className={cn(
        "flex items-center gap-2 overflow-x-auto",
        "scrollbar-thin scrollbar-track-transparent",
        isScrolling ? "scrollbar-thumb-black/50" : "scrollbar-thumb-transparent",
        !hasOverflow && "pb-2",
        className
      )}
    >
      {ranges.map((range, i) => (
        <AvailabilityRangeButton
          key={range.start.getTime()}
          timezone={timezone}
          weekStart={weekStart}
          range={range}
          onClick={getAvailabilityRangeClick(range.start)}
          couldBeNow={i === 0}
        />
      ))}
    </section>
  );
};
